import { StepType } from "@redotech/redo-flows-core-schema-common/common";
import { ObjectId } from "bson";
import { stepSchema, type FlowStep } from "./advanced-flow-db-parser";
import {
  isInsertablePublicAutomationStepForSchema,
  MARKETING_AUTOMATION_PUBLIC_STEP,
} from "./automation-step-catalog";

export type StepOpErrorCode =
  | "STEP_NOT_FOUND"
  | "STEP_TYPE_MISMATCH"
  | "INVALID_ANCHOR"
  | "INVALID_MOVE"
  | "INVALID_STEP_PATCH"
  | "INVALID_OPERATION"
  | "STEP_NOT_INSERTABLE"
  | "STEP_NOT_REMOVABLE";

export class StepOpError extends Error {
  readonly code: StepOpErrorCode;

  constructor(code: StepOpErrorCode, message: string) {
    super(message);
    this.name = "StepOpError";
    this.code = code;
  }
}

export interface StepUpdateOp {
  update: { id: string; type: StepType; patch: Record<string, unknown> };
}

export interface StepInsertOp {
  insert: {
    after?: string;
    before?: string;
    branch?: "true" | "false";
    variantId?: string;
    step: FlowStep;
  };
}

export interface StepRemoveOp {
  remove: { id: string };
}

export interface StepMoveOp {
  move: {
    id: string;
    after?: string;
    before?: string;
    branch?: "true" | "false";
    variantId?: string;
  };
}

export type StepOperation =
  | StepUpdateOp
  | StepInsertOp
  | StepRemoveOp
  | StepMoveOp;

export function mintStepId(): string {
  return new ObjectId().toString();
}

export function deepMerge(
  target: Record<string, unknown>,
  patch: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...target };
  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined) {
      continue;
    }
    const existing = out[key];
    if (
      value != null &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      existing != null &&
      typeof existing === "object" &&
      !Array.isArray(existing)
    ) {
      out[key] = deepMerge(
        existing as Record<string, unknown>,
        value as Record<string, unknown>,
      );
    } else {
      out[key] = value;
    }
  }
  return out;
}

function asRecord(step: FlowStep): Record<string, unknown> {
  return step as unknown as Record<string, unknown>;
}

function stepId(step: FlowStep): string {
  return step.id;
}

function findIndex(steps: FlowStep[], id: string): number {
  return steps.findIndex((step) => stepId(step) === id);
}

function requireStep(steps: FlowStep[], id: string): FlowStep {
  const step = steps.find((candidate) => stepId(candidate) === id);
  if (!step) {
    throw new StepOpError("STEP_NOT_FOUND", `Step ${id} was not found`);
  }
  return step;
}

function triggerCount(steps: FlowStep[]): number {
  return steps.filter((step) => step.type === StepType.TRIGGER).length;
}

const LINEAR_NEXT_TYPES = new Set<string>([
  StepType.TRIGGER,
  StepType.WAIT,
  StepType.WAIT_FOR_EVENT,
  StepType.SEND_EMAIL,
  StepType.SEND_SMS,
  StepType.SEND_WEBHOOK,
  StepType.MANAGE_CUSTOMER_TAGS,
  StepType.MANAGE_STATIC_SEGMENT,
  StepType.SHOPPER_AGENT_MESSAGE,
  StepType.RECOVER,
  StepType.CONCIERGE_BLOCK,
]);

function readString(step: FlowStep, key: string): string | undefined {
  const value = asRecord(step)[key];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function writeString(
  step: FlowStep,
  key: string,
  value: string | undefined,
): void {
  if (value == null) {
    delete asRecord(step)[key];
    return;
  }
  asRecord(step)[key] = value;
}

function primaryOutgoing(step: FlowStep): string | undefined {
  const nextId = readString(step, "nextId");
  if (nextId) {
    return nextId;
  }
  const nextTrueId = readString(step, "nextTrueId");
  if (nextTrueId) {
    return nextTrueId;
  }
  const variants = asRecord(step).variants;
  if (Array.isArray(variants)) {
    for (const variant of variants) {
      if (
        variant != null &&
        typeof variant === "object" &&
        typeof (variant as { nextId?: unknown }).nextId === "string"
      ) {
        const id = (variant as { nextId: string }).nextId;
        if (id.length > 0) {
          return id;
        }
      }
    }
  }
  return undefined;
}

function replaceIncoming(
  steps: FlowStep[],
  oldId: string,
  newId: string | undefined,
): void {
  for (const step of steps) {
    if (readString(step, "nextId") === oldId) {
      writeString(step, "nextId", newId);
    }
    if (readString(step, "nextTrueId") === oldId) {
      writeString(step, "nextTrueId", newId);
    }
    if (readString(step, "nextFalseId") === oldId) {
      writeString(step, "nextFalseId", newId);
    }
    const variants = asRecord(step).variants;
    if (Array.isArray(variants)) {
      for (const variant of variants) {
        if (
          variant != null &&
          typeof variant === "object" &&
          (variant as { nextId?: unknown }).nextId === oldId
        ) {
          (variant as { nextId: string | undefined }).nextId = newId;
        }
      }
    }
  }
}

interface Anchor {
  parent: FlowStep;
  branch?: "true" | "false";
  variantId?: string;
}

function uniqueIncoming(steps: FlowStep[], childId: string): FlowStep {
  const parents = steps.filter((step) => {
    if (readString(step, "nextId") === childId) {
      return true;
    }
    if (readString(step, "nextTrueId") === childId) {
      return true;
    }
    if (readString(step, "nextFalseId") === childId) {
      return true;
    }
    const variants = asRecord(step).variants;
    if (Array.isArray(variants)) {
      return variants.some(
        (variant) =>
          variant != null &&
          typeof variant === "object" &&
          (variant as { nextId?: unknown }).nextId === childId,
      );
    }
    return false;
  });
  if (parents.length !== 1) {
    throw new StepOpError(
      "INVALID_ANCHOR",
      `Step ${childId} must have exactly one incoming edge to use as a before/move anchor`,
    );
  }
  return parents[0]!;
}

function resolveAnchor(
  steps: FlowStep[],
  op: {
    after?: string;
    before?: string;
    branch?: "true" | "false";
    variantId?: string;
  },
): Anchor {
  if (op.after != null && op.before != null) {
    throw new StepOpError("INVALID_ANCHOR", "Pass after or before, not both");
  }
  if (op.after == null && op.before == null) {
    throw new StepOpError(
      "INVALID_ANCHOR",
      "Insert and move require after (parent step) or before (existing child)",
    );
  }
  if (op.before != null) {
    const child = requireStep(steps, op.before);
    const parent = uniqueIncoming(steps, child.id);
    return inferAnchor(parent, child.id, op);
  }
  const parent = requireStep(steps, op.after!);
  return inferAnchor(parent, undefined, op);
}

function inferAnchor(
  parent: FlowStep,
  childId: string | undefined,
  op: { branch?: "true" | "false"; variantId?: string },
): Anchor {
  if (parent.type === StepType.DO_NOTHING) {
    throw new StepOpError(
      "INVALID_ANCHOR",
      "An end step has no outgoing edge to attach to",
    );
  }
  if (parent.type === StepType.CONDITION) {
    let branch = op.branch;
    if (branch == null && childId != null) {
      if (readString(parent, "nextTrueId") === childId) {
        branch = "true";
      } else if (readString(parent, "nextFalseId") === childId) {
        branch = "false";
      }
    }
    if (branch !== "true" && branch !== "false") {
      throw new StepOpError(
        "INVALID_ANCHOR",
        "Condition steps require branch TRUE or FALSE",
      );
    }
    return { parent, branch };
  }
  if (parent.type === StepType.AB_TEST) {
    const variants = asRecord(parent).variants;
    if (!Array.isArray(variants) || variants.length === 0) {
      throw new StepOpError("INVALID_ANCHOR", "A/B test has no variants");
    }
    let variantId = op.variantId;
    if (variantId == null && childId != null) {
      const match = variants.find(
        (variant) =>
          variant != null &&
          typeof variant === "object" &&
          (variant as { nextId?: unknown }).nextId === childId,
      ) as { id?: string } | undefined;
      variantId = match?.id;
    }
    if (variantId == null) {
      throw new StepOpError(
        "INVALID_ANCHOR",
        "A/B test steps require variantId",
      );
    }
    return { parent, variantId };
  }
  return { parent };
}

function takeOutgoing(anchor: Anchor): string | undefined {
  const { parent } = anchor;
  if (parent.type === StepType.CONDITION) {
    const key = anchor.branch === "true" ? "nextTrueId" : "nextFalseId";
    const previous = readString(parent, key);
    return previous;
  }
  if (parent.type === StepType.AB_TEST) {
    const variants = asRecord(parent).variants;
    if (!Array.isArray(variants)) {
      return undefined;
    }
    const variant = variants.find(
      (item) =>
        item != null &&
        typeof item === "object" &&
        (item as { id?: unknown }).id === anchor.variantId,
    ) as { nextId?: string } | undefined;
    if (!variant) {
      throw new StepOpError(
        "INVALID_ANCHOR",
        `A/B test variant ${anchor.variantId} was not found`,
      );
    }
    return variant.nextId;
  }
  if (
    !LINEAR_NEXT_TYPES.has(parent.type) &&
    readString(parent, "nextId") == null
  ) {
    throw new StepOpError(
      "INVALID_ANCHOR",
      `Cannot attach a step after ${parent.type}`,
    );
  }
  return readString(parent, "nextId");
}

function setOutgoing(anchor: Anchor, nextId: string): void {
  const { parent } = anchor;
  if (parent.type === StepType.CONDITION) {
    writeString(
      parent,
      anchor.branch === "true" ? "nextTrueId" : "nextFalseId",
      nextId,
    );
    return;
  }
  if (parent.type === StepType.AB_TEST) {
    const variants = asRecord(parent).variants;
    if (!Array.isArray(variants)) {
      throw new StepOpError("INVALID_ANCHOR", "A/B test has no variants");
    }
    const variant = variants.find(
      (item) =>
        item != null &&
        typeof item === "object" &&
        (item as { id?: unknown }).id === anchor.variantId,
    ) as { nextId?: string } | undefined;
    if (!variant) {
      throw new StepOpError(
        "INVALID_ANCHOR",
        `A/B test variant ${anchor.variantId} was not found`,
      );
    }
    variant.nextId = nextId;
    return;
  }
  writeString(parent, "nextId", nextId);
}

function wireInsertedOutgoing(
  inserted: FlowStep,
  previousNext: string | undefined,
  steps: FlowStep[],
): void {
  if (inserted.type === StepType.DO_NOTHING) {
    return;
  }
  if (inserted.type === StepType.CONDITION) {
    if (!readString(inserted, "nextTrueId") && previousNext) {
      writeString(inserted, "nextTrueId", previousNext);
    }
    if (!readString(inserted, "nextFalseId")) {
      const end: FlowStep = {
        id: mintStepId(),
        type: StepType.DO_NOTHING,
      } as FlowStep;
      steps.push(end);
      writeString(inserted, "nextFalseId", end.id);
    }
    return;
  }
  if (inserted.type === StepType.AB_TEST) {
    const variants = asRecord(inserted).variants;
    if (!Array.isArray(variants) || variants.length === 0) {
      const end: FlowStep = {
        id: mintStepId(),
        type: StepType.DO_NOTHING,
      } as FlowStep;
      steps.push(end);
      asRecord(inserted).variants = [
        {
          id: mintStepId(),
          name: "Variant A",
          weight: 50,
          nextId: previousNext ?? end.id,
        },
        { id: mintStepId(), name: "Variant B", weight: 50, nextId: end.id },
      ];
      if (!previousNext) {
        const extra: FlowStep = {
          id: mintStepId(),
          type: StepType.DO_NOTHING,
        } as FlowStep;
        steps.push(extra);
        (asRecord(inserted).variants as { nextId: string }[])[0]!.nextId =
          extra.id;
      }
      return;
    }
    if (previousNext && variants[0] && typeof variants[0] === "object") {
      const first = variants[0] as { nextId?: string };
      if (!first.nextId) {
        first.nextId = previousNext;
      }
    }
    return;
  }
  if (
    (LINEAR_NEXT_TYPES.has(inserted.type) || "nextId" in inserted) &&
    !readString(inserted, "nextId") &&
    previousNext
  ) {
    writeString(inserted, "nextId", previousNext);
  }
}

function parseStep(step: FlowStep): FlowStep {
  const parsed = stepSchema.safeParse(step);
  if (!parsed.success) {
    throw new StepOpError(
      "INVALID_STEP_PATCH",
      parsed.error.issues.map((issue) => issue.message).join("; "),
    );
  }
  return parsed.data;
}

function applyUpdate(steps: FlowStep[], op: StepUpdateOp["update"]): void {
  const index = findIndex(steps, op.id);
  if (index < 0) {
    throw new StepOpError("STEP_NOT_FOUND", `Step ${op.id} was not found`);
  }
  const existing = steps[index]!;
  if (existing.type !== op.type) {
    throw new StepOpError(
      "STEP_TYPE_MISMATCH",
      `Step ${op.id} is ${existing.type}, not ${op.type}`,
    );
  }
  const spec = MARKETING_AUTOMATION_PUBLIC_STEP[op.type];
  if (spec.treatment !== "typed") {
    throw new StepOpError(
      "STEP_TYPE_MISMATCH",
      `Step type ${op.type} cannot be updated through this API`,
    );
  }
  const { type: _type, id: _id, ...rest } = op.patch;
  if (existing.type === StepType.TRIGGER) {
    delete rest.schemaType;
    delete rest.key;
    delete rest.category;
    delete rest.type;
  }
  const merged = deepMerge(asRecord(existing), rest);
  merged.type = existing.type;
  merged.id = existing.id;
  steps[index] = parseStep(merged as FlowStep);
}

function triggerSchemaType(steps: FlowStep[]): string {
  const trigger = steps.find((step) => step.type === StepType.TRIGGER);
  if (trigger == null) {
    return "";
  }
  const schemaType = asRecord(trigger).schemaType;
  return typeof schemaType === "string" ? schemaType : "";
}

function applyInsert(steps: FlowStep[], op: StepInsertOp["insert"]): void {
  if (op.step.type === StepType.TRIGGER) {
    throw new StepOpError(
      "STEP_NOT_INSERTABLE",
      "A flow already has a trigger; the public API cannot insert another",
    );
  }
  if (
    !isInsertablePublicAutomationStepForSchema(
      op.step.type,
      triggerSchemaType(steps),
    )
  ) {
    throw new StepOpError(
      "STEP_NOT_INSERTABLE",
      `Step type ${op.step.type} cannot be inserted on this automation's trigger schema`,
    );
  }
  if (findIndex(steps, op.step.id) >= 0) {
    throw new StepOpError(
      "INVALID_STEP_PATCH",
      `Step id ${op.step.id} is already in the flow`,
    );
  }
  const inserted = structuredClone(op.step);
  const anchor = resolveAnchor(steps, op);
  const previous = takeOutgoing(anchor);
  steps.push(inserted);
  wireInsertedOutgoing(inserted, previous, steps);
  setOutgoing(anchor, inserted.id);
  const parsed = parseStep(inserted);
  const index = findIndex(steps, parsed.id);
  steps[index] = parsed;
  for (const [i, step] of steps.entries()) {
    if (step.type === StepType.DO_NOTHING) {
      steps[i] = parseStep(step);
    }
  }
}

function collectSubtree(
  steps: FlowStep[],
  rootId: string,
  seen: Set<string>,
): void {
  if (seen.has(rootId)) {
    return;
  }
  seen.add(rootId);
  const step = steps.find((candidate) => candidate.id === rootId);
  if (!step) {
    return;
  }
  const nextId = readString(step, "nextId");
  if (nextId) {
    collectSubtree(steps, nextId, seen);
  }
  const nextTrueId = readString(step, "nextTrueId");
  if (nextTrueId) {
    collectSubtree(steps, nextTrueId, seen);
  }
  const nextFalseId = readString(step, "nextFalseId");
  if (nextFalseId) {
    collectSubtree(steps, nextFalseId, seen);
  }
  const variants = asRecord(step).variants;
  if (Array.isArray(variants)) {
    for (const variant of variants) {
      if (
        variant != null &&
        typeof variant === "object" &&
        typeof (variant as { nextId?: unknown }).nextId === "string"
      ) {
        collectSubtree(steps, (variant as { nextId: string }).nextId, seen);
      }
    }
  }
}

function applyRemove(steps: FlowStep[], id: string): FlowStep[] {
  const step = requireStep(steps, id);
  if (step.type === StepType.TRIGGER) {
    throw new StepOpError(
      "STEP_NOT_REMOVABLE",
      "The trigger step cannot be removed",
    );
  }
  const keep = primaryOutgoing(step);
  const drop = new Set<string>();
  if (step.type === StepType.CONDITION) {
    const falseId = readString(step, "nextFalseId");
    if (falseId) {
      collectSubtree(steps, falseId, drop);
    }
  }
  if (step.type === StepType.AB_TEST) {
    const variants = asRecord(step).variants;
    if (Array.isArray(variants)) {
      for (const variant of variants) {
        if (
          variant != null &&
          typeof variant === "object" &&
          typeof (variant as { nextId?: unknown }).nextId === "string"
        ) {
          const nextId = (variant as { nextId: string }).nextId;
          if (nextId !== keep) {
            collectSubtree(steps, nextId, drop);
          }
        }
      }
    }
  }
  replaceIncoming(steps, id, keep);
  return steps.filter(
    (candidate) => candidate.id !== id && !drop.has(candidate.id),
  );
}

function applyMove(steps: FlowStep[], op: StepMoveOp["move"]): void {
  const step = requireStep(steps, op.id);
  if (step.type === StepType.TRIGGER) {
    throw new StepOpError("INVALID_MOVE", "The trigger step cannot be moved");
  }
  const keep = primaryOutgoing(step);
  replaceIncoming(steps, op.id, keep);
  const anchor = resolveAnchor(steps, op);
  if (anchor.parent.id === op.id) {
    throw new StepOpError(
      "INVALID_MOVE",
      "A step cannot be attached to itself",
    );
  }
  const previous = takeOutgoing(anchor);
  if (LINEAR_NEXT_TYPES.has(step.type) || "nextId" in step) {
    writeString(step, "nextId", previous);
  } else if (step.type === StepType.CONDITION && previous) {
    writeString(step, "nextTrueId", previous);
  }
  setOutgoing(anchor, step.id);
}

export function applyStepOperations(
  steps: FlowStep[],
  operations: readonly StepOperation[],
): FlowStep[] {
  let next = structuredClone(steps);
  for (const operation of operations) {
    const kinds = (["update", "insert", "remove", "move"] as const).filter(
      (key) => key in operation,
    );
    if (kinds.length !== 1) {
      throw new StepOpError(
        "INVALID_OPERATION",
        "Each operation must be exactly one of update, insert, remove, or move",
      );
    }
    if ("update" in operation) {
      applyUpdate(next, operation.update);
    } else if ("insert" in operation) {
      applyInsert(next, operation.insert);
    } else if ("remove" in operation) {
      next = applyRemove(next, operation.remove.id);
    } else {
      applyMove(next, operation.move);
    }
  }
  if (triggerCount(next) !== 1) {
    throw new StepOpError(
      "INVALID_OPERATION",
      "Flow must have exactly one trigger step",
    );
  }
  return next;
}
